<template>
  <!-- What the extension looks like when you click the toolbar icon, rebuilt in
       the page rather than shipped as a screenshot.

       Three reasons it's markup: a PNG of a 520x560 popup is the largest thing
       this page would download and it would land in the LCP, the text in it
       would be invisible to a crawler, and a screenshot goes stale the first
       time the popup changes. Every value below is real output from the
       extension's own palette for a page like this one.

       It is decorative in the sense that nothing here is clickable — the one
       thing to press on this page is the install button — but the copy is left
       readable rather than aria-hidden, because "custom / screenshot / code
       colors" is exactly what the page is claiming and a screen reader should
       get to hear it too. -->
  <figure class="relative mx-auto w-full max-w-[27rem]">
    <!-- Ambient wash, the same shape the /api hero and its bento cards use. -->
    <div class="pointer-events-none absolute -inset-8 -z-10 transform-gpu blur-3xl" aria-hidden="true">
      <div
        class="aspect-[1155/678] h-full w-full bg-gradient-to-br from-sky-300 to-indigo-600 opacity-[0.12]"
        style="clip-path: polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)"
      />
    </div>

    <!-- Browser chrome: enough of it to say "this is a toolbar popup", with the
         extension's own icon lit up as the thing that was just clicked. -->
    <div class="overflow-hidden rounded-t-xl border-x-2 border-t-2 border-gray-800 bg-gray-900">
      <div class="flex items-center gap-3 px-3 py-2.5">
        <div class="flex gap-1.5" aria-hidden="true">
          <span class="h-2.5 w-2.5 rounded-full bg-gray-700"></span>
          <span class="h-2.5 w-2.5 rounded-full bg-gray-700"></span>
          <span class="h-2.5 w-2.5 rounded-full bg-gray-700"></span>
        </div>
        <div class="flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-gray-950 px-2 py-1">
          <LockClosedIcon class="h-3 w-3 shrink-0 text-gray-600" aria-hidden="true" />
          <span class="truncate text-2xs text-gray-500">{{ site }}</span>
        </div>
        <span
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-500/20 ring-1 ring-inset ring-indigo-500/40"
          aria-hidden="true"
        >
          <img :src="paletteIcon" width="14" height="14" alt="" class="h-3.5 w-3.5" />
        </span>
      </div>
    </div>

    <!-- The popup itself. -->
    <div class="rounded-b-xl border-2 border-gray-800 bg-gray-950 px-4 pb-4 pt-3 shadow-2xl shadow-black/60">
      <div class="flex items-center gap-2">
        <img :src="logo" width="132" height="20" alt="colorize.design" class="h-5 w-auto" />
        <ArrowTopRightOnSquareIcon class="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
        <span class="ml-auto text-2xs text-gray-500">{{ totalColors }} colors</span>
        <Cog6ToothIcon class="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
      </div>

      <!-- The four actions across the top of the real popup. -->
      <div class="mt-3 grid grid-cols-4 gap-1.5">
        <div
          v-for="action in actions"
          :key="action.label"
          class="flex flex-col items-center gap-1 rounded-lg border border-gray-800 bg-gray-900 px-1 py-2"
        >
          <component :is="action.icon" class="h-4 w-4 text-gray-300" aria-hidden="true" />
          <span class="text-2xs font-medium leading-3 text-gray-400">{{ action.label }}</span>
        </div>
      </div>

      <div class="mt-4 space-y-3.5">
        <section v-for="section in sections" :key="section.title">
          <div class="flex items-baseline gap-2">
            <h3 class="text-xs font-semibold text-gray-200">{{ section.title }}</h3>
            <span
              class="rounded px-1.5 py-0.5 text-2xs font-medium"
              :class="section.badge === 'custom' ? 'bg-indigo-500/15 text-indigo-300' : 'bg-white/5 text-gray-400'"
            >{{ section.badge }}</span>
            <p class="ml-auto text-2xs text-gray-500">{{ section.hint }}</p>
          </div>

          <div class="mt-1.5 grid grid-cols-4 gap-1.5">
            <!-- The site's swatch (atoms/ColorSwatch), at popup scale. -->
            <div
              v-for="swatch in section.swatches"
              :key="swatch.hex"
              class="overflow-hidden rounded-lg border-2 border-gray-900 bg-white shadow"
            >
              <ColorSwatch :color="swatch.hex" size="sm" />
              <div class="relative -mt-px bg-white px-1.5 pb-1">
                <p class="truncate text-2xs font-bold leading-4 text-gray-600">{{ swatch.hex }}</p>
                <p class="truncate text-2xs leading-4 text-gray-500">{{ swatch.name }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </figure>
</template>

<script setup>
import { computed } from 'vue'
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
  EyeDropperIcon,
  LockClosedIcon,
  PencilSquareIcon,
  ShareIcon,
} from '@heroicons/vue/24/outline'
import ColorSwatch from '~/components/atoms/ColorSwatch.vue'
import logo from '~/assets/img/colorize_design_logo.svg'
import paletteIcon from '~/assets/img/palettes_icon.svg'

defineProps({
  // The address shown in the mocked toolbar. A page behind a login is the point
  // being made, so the default is one.
  site: { type: String, default: 'app.example.com/dashboard' },
})

const actions = [
  { label: 'Download', icon: ArrowDownTrayIcon },
  { label: 'Picker', icon: EyeDropperIcon },
  { label: 'Share', icon: ShareIcon },
  { label: 'Edit', icon: PencilSquareIcon },
]

// One row per section, in the popup's own four-column grid. The real popup
// scrolls a much longer palette; a hero illustration that reproduced all of it
// would be taller than the copy beside it and would push the install button
// off the fold on anything short of a desktop.
const sections = [
  {
    title: 'Custom colors',
    badge: 'custom',
    hint: 'your picks',
    swatches: [
      { hex: '#4F46E5', name: 'royalblue' },
      { hex: '#F97316', name: 'chocolate' },
    ],
  },
  {
    title: 'Screenshot colors',
    badge: 'local',
    hint: 'visible area',
    swatches: [
      { hex: '#0B1120', name: 'black' },
      { hex: '#7C3AED', name: 'blueviolet' },
      { hex: '#E2E8F0', name: 'lavender' },
    ],
  },
  {
    title: 'Code colors',
    badge: 'local',
    hint: 'html + css',
    swatches: [
      { hex: '#FFFFFF', name: 'white' },
      { hex: '#6366F1', name: 'slateblue' },
      { hex: '#F43F5E', name: 'crimson' },
      { hex: '#FACC15', name: 'gold' },
    ],
  },
]

const totalColors = computed(() =>
  sections.reduce((total, section) => total + section.swatches.length, 0),
)
</script>
